import { Search, Building, FileCheck } from "lucide-react";

const features = [
  {
    icon: Search,
    title: "Independent Title Verification",
    description: "Our firm conducts comprehensive investigations into property titles, land registry records, ownership history, and encumbrances to confirm that the property can be legally transferred."
  },
  {
    icon: Building,
    title: "Land Registry and Government Search",
    description: "We verify records with the relevant land registries and authorities to identify government acquisition risks, disputes, or competing ownership claims."
  },
  {
    icon: FileCheck,
    title: "Transaction Supervision and Legal Documentation",
    description: "We supervise the entire acquisition process, prepare and review legal documentation, and ensure the transaction is completed lawfully and securely."
  }
];

export const FraudProtectionSection = () => {
  return (
    <section 
      className="py-16 md:py-24 lg:py-32 bg-[#0A0A0B] relative overflow-hidden"
      data-testid="fraud-protection-section"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      
      {/* Corner accents */}
      <div className="absolute top-12 left-12 w-20 h-20 border-l border-t border-[#D4AF37]/15 hidden lg:block" />
      <div className="absolute bottom-12 right-12 w-20 h-20 border-r border-b border-[#D4AF37]/15 hidden lg:block" />
      
      <div className="relative z-10 max-w-6xl mx-auto px-5 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <span className="text-[#D4AF37] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-semibold">
            Risk Mitigation
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-white mt-4 md:mt-6 leading-tight">
            Protecting Investors from
            <span className="block text-[#D4AF37] italic">Property Fraud</span>
          </h2>
          <div className="w-16 md:w-20 h-px bg-[#D4AF37] mx-auto mt-6 md:mt-8" />
          
          {/* Introduction text */}
          <p className="max-w-3xl mx-auto mt-8 md:mt-10 text-white/60 text-sm md:text-base lg:text-lg leading-relaxed">
            Property fraud and defective land titles remain one of the biggest risks facing property 
            investors in Nigeria, particularly for diaspora clients purchasing property remotely. 
            Our firm provides rigorous legal due diligence to ensure every transaction is secure, 
            transparent, and legally valid.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="group p-6 md:p-8 border border-[#D4AF37]/15 hover:border-[#D4AF37]/40 bg-[#0A0A0B]/50 transition-all duration-300"
              data-testid={`fraud-feature-${index}`}
            >
              <div className="w-14 h-14 md:w-16 md:h-16 border border-[#D4AF37]/30 flex items-center justify-center mb-5 md:mb-6 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-all duration-300">
                <feature.icon className="w-6 h-6 md:w-7 md:h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-lg md:text-xl text-white mb-3 md:mb-4">
                {feature.title}
              </h3>
              <p className="text-white/50 text-sm md:text-base leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Highlighted statement */}
        <div className="mt-12 md:mt-16 lg:mt-20">
          <div className="relative border border-[#D4AF37]/30 p-6 md:p-8 lg:p-10 text-center bg-[#D4AF37]/5">
            {/* Gold corner accents */}
            <div className="absolute top-0 left-0 w-6 h-6 md:w-8 md:h-8 border-l-2 border-t-2 border-[#D4AF37]" />
            <div className="absolute top-0 right-0 w-6 h-6 md:w-8 md:h-8 border-r-2 border-t-2 border-[#D4AF37]" />
            <div className="absolute bottom-0 left-0 w-6 h-6 md:w-8 md:h-8 border-l-2 border-b-2 border-[#D4AF37]" />
            <div className="absolute bottom-0 right-0 w-6 h-6 md:w-8 md:h-8 border-r-2 border-b-2 border-[#D4AF37]" />
            
            <p className="font-['Playfair_Display'] text-lg md:text-xl lg:text-2xl text-[#D4AF37] italic leading-relaxed max-w-3xl mx-auto">
              "Every property transaction handled by our firm undergoes independent legal verification before completion."
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
